import React, { useState, useRef, useEffect, useLayoutEffect, useId } from "react";
import { createPortal } from "react-dom";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Box, IconButton, InputAdornment } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import RewampTextField from "./RewampTextField";

const DROPDOWN_GAP = 4;

export default function RewampDateInput({
  value,
  onChange,
  disabled = false,
  error = false,
  maxDate = new Date(new Date().setFullYear(new Date().getFullYear())),
  minDate = new Date(1900, 0, 1),
  inputId,
  ariaLabel,
  ariaDescribedBy,
  separator = "-",
  placeholder,
  iconColor,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const anchorRef = useRef(null);
  const calendarRef = useRef(null);
  const generatedId = useId();
  const id = inputId || generatedId;

  function handleChange(newDate) {
    if (disabled) return;
    setIsOpen(false);
    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, "0");
    const day = String(newDate.getDate()).padStart(2, "0");
    onChange(`${year}-${month}-${day}`);
  }

  function toggleCalendar() {
    if (disabled) return;
    setIsOpen(!isOpen);
  }

  function formatDate(dateString) {
    if (!dateString) return "";
    return new Date(dateString)
      .toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      .replace(/\//g, separator);
  }

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        anchorRef.current?.contains(event.target) ||
        calendarRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Portaled to <body> and positioned from the input's rect so an ancestor
  // `overflow: hidden` can't clip it. Flips up when there's no room below.
  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) return;

    const updatePosition = () => {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const calendarHeight = calendarRef.current?.offsetHeight || 350;
      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const openUpward = spaceBelow < calendarHeight && anchorRect.top > spaceBelow;

      setPosition({
        left: anchorRect.left,
        width: anchorRect.width,
        top: openUpward
          ? anchorRect.top - calendarHeight - DROPDOWN_GAP
          : anchorRect.bottom + DROPDOWN_GAP,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  return (
    <Box ref={anchorRef} sx={{ position: "relative", minWidth: "min(40vh, 30%)" }}>
      <RewampTextField
        id={id}
        type="text"
        value={formatDate(value)}
        onClick={toggleCalendar}
        placeholder={placeholder ?? t("SELECT_Date")}
        disabled={disabled}
        error={error}
        fullWidth
        inputProps={ariaLabel || ariaDescribedBy ? { "aria-label": ariaLabel, "aria-describedby": ariaDescribedBy } : undefined}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end" sx={{ zIndex: 2 }}>
              <IconButton aria-label={t("SELECT_Date")} disabled={disabled} sx={iconColor ? { color: iconColor } : undefined}>
                <CalendarMonthIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {isOpen &&
        createPortal(
          <Box
            ref={calendarRef}
            sx={{
              position: "fixed",
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              width: position?.width,
              minWidth: "30vh",
              zIndex: theme.zIndex.modal + 1,
              visibility: position ? "visible" : "hidden",
            }}
          >
            <Calendar
              date={value ? new Date(value) : maxDate}
              onChange={handleChange}
              minDate={minDate}
              maxDate={maxDate}
            />
          </Box>,
          document.body
        )}
    </Box>
  );
}

RewampDateInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  inputId: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  separator: PropTypes.string,
  placeholder: PropTypes.string,
  iconColor: PropTypes.string,
};
